import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { FormActionResult } from "@coso/connection-forms";
import { FormText, FormBuilder, FormGroup } from "@coso/forms";
import { LoadingService } from "@coso/loading";
import { BehaviorSubject, forkJoin } from "rxjs";
import { filter, map, take } from "rxjs/operators";
import { HighAvailabilityModes } from "../../../models";
import { AUserPermissionsService } from "../../../services/a-user-permissions.service";
import { ActiveDirectoryConnectionService } from "../../../services/active-directory-connection.service";
import { ReplicationService } from "../../../services/replication.service";
import { SettingsService } from "../../../services/settings.service";
import { handleError } from "../../../utils/handle-error";
import { isRunning } from "../../../utils/is-running";

export interface ActiveDirectoryConnectionForm {
  server: string;
  username: string;
  password: string;
  organizationalUnitName?: string;
}

@Component({
  standalone:false,
  templateUrl: "./active-directory.component.html",
  styleUrls: ["./active-directory.component.scss"]
})
export class ActiveDirectoryComponent implements OnInit {
  @ViewChild("serverPopover", { static: true }) public serverPopover: TemplateRef<any>;
  @ViewChild("organizationalUnitPopover", { static: true }) public organizationalUnitPopover: TemplateRef<any>;

  instanceId: string;
  get title() {
    if (this.isConfigured) {
      if (this.isReadOnly) {
        return "View Active Directory connection";
      } else {
        return "Edit Active Directory connection";
      }
    } else {
      return "Create Active Directory connection";
    }
  }
  private isTestingConnection = new BehaviorSubject<boolean>(false);
  private testConnectionResult = new BehaviorSubject<FormActionResult>(undefined);
  get isTestingConnection$() {
    return this.isTestingConnection.asObservable();
  }
  get testConnectionResult$() {
    return this.testConnectionResult.asObservable();
  }
  get isTestConnectionSuccessful$() {
    return this.testConnectionResult.asObservable().pipe(map((r) => r && r.isSuccess));
  }
  errorMessage: string;
  isConfigured = false;
  isReadOnly: boolean;
  isRunning: boolean;

  formGroup: FormGroup<ActiveDirectoryConnectionForm>;
  formText: FormText<ActiveDirectoryConnectionForm>;

  constructor(
    public loadingService: LoadingService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private userPermissionsService: AUserPermissionsService,
    private connectionService: ActiveDirectoryConnectionService,
    private replicationService: ReplicationService,
    private settingsService: SettingsService
  ) {}

  ngOnInit() {
    this.instanceId = this.route.snapshot.paramMap.get("instance-id");
    forkJoin([this.replicationService.getStatus(this.instanceId), this.userPermissionsService.hasLimitedAccess$, this.settingsService.get(this.instanceId)]).subscribe(
      ([status, hasLimitedAccess, settings]) => {
        this.isRunning = isRunning(status);
        this.isReadOnly = hasLimitedAccess || isRunning(status) || settings.highAvailabilityMode === HighAvailabilityModes.FailoverBackup;
      },
      (error) => (this.errorMessage = `Something went wrong due to: ${handleError(error)}`)
    );
    this.generateFromGroup();
    this.loadConnection();
  }

  returnToList(message?: string) {
    this.router.navigate(["../"], {
      relativeTo: this.route,
      state: { message }
    });
  }

  testConnection() {
    if (this.formGroup.valid) {
      this.isTestingConnection.next(true);
      this.testConnectionResult.next(undefined);
      const holder = {
        organizationalUnitName: this.formGroup.controls.organizationalUnitName.value,
        connectionData: {
          server: this.formGroup.controls.server.value,
          user: this.formGroup.controls.username.value,
          password: this.formGroup.controls.password.value,
          authentication: null,
          accessToken: null
        }
      };
      this.connectionService.testConnection(this.instanceId, holder).subscribe(
        (result) => {
          this.isTestingConnection.next(false);
          if (result.success) {
            this.testConnectionResult.next({
              isSuccess: true,
              message: `Test Active Directory connection succeeded.`
            });
          } else {
            this.testConnectionResult.next({
              isSuccess: false,
              message: `Test Active Directory connection failed due to: ${result.error}`
            });
          }
        },
        (error) => {
          this.isTestingConnection.next(false);
          this.testConnectionResult.next({
            isSuccess: false,
            message: `Test Active Directory connection failed due to: ${handleError(error)}`
          });
        }
      );
    } else {
      this.formGroup.markAllAsTouched();
    }
  }

  saveConnection() {
    if (this.isReadOnly) return;

    if (this.formGroup.valid) {
      this.testConnectionResult$
        .pipe(
          filter((r) => !!r && r.isSuccess),
          take(1)
        )
        .subscribe(() => this.save());
      this.isTestConnectionSuccessful$.pipe(take(1)).subscribe((result) => {
        if (!result) {
          this.testConnection();
        }
      });
    } else {
      this.formGroup.markAllAsTouched();
    }
  }

  onInputChange() {
    this.testConnectionResult.next(undefined);
    this.isTestingConnection.next(false);
  }

  private save() {
    this.loadingService.start("Saving Active Directory connection", "saveConnection");
    const holder = {
      organizationalUnitName: this.formGroup.controls.organizationalUnitName.value,
      connectionData: {
        server: this.formGroup.controls.server.value,
        user: this.formGroup.controls.username.value,
        password: this.formGroup.controls.password.value,
        authentication: null,
        accessToken: null
      }
    };
    this.connectionService.upsertConnection(this.instanceId, holder).subscribe(
      () => {
        this.returnToList("Active Directory connection has been saved successfully.");
        this.loadingService.stop("saveConnection");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("saveConnection");
      }
    );
  }

  private loadConnection() {
    this.loadingService.start("Loading Active Directory connection", "loadAdConnection");
    this.connectionService.getConnection(this.instanceId).subscribe(
      (data) => {
        this.isConfigured = !!data.connectionData.server;
        if (this.isConfigured) {
          this.formGroup.controls.password.clearValidators();
        } else {
          this.formGroup.controls.password.setValidators(Validators.required);
        }
        this.formGroup.patchValue({
          server: data.connectionData.server,
          username: data.connectionData.user,
          password: data.connectionData.password,
          organizationalUnitName: data.organizationalUnitName
        });
        this.formGroup.updateValueAndValidity();
        this.loadingService.stop("loadAdConnection");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("loadAdConnection");
      }
    );
  }

  private generateFromGroup() {
    this.formGroup = this.fb.group<ActiveDirectoryConnectionForm>({
      server: this.fb.control(undefined, [Validators.required]),
      username: this.fb.control(undefined, [Validators.required]),
      password: this.fb.control(undefined, [Validators.required]),
      organizationalUnitName: this.fb.control(undefined)
    });
    this.formText = {
      server: {
        label: "Server URL",
        popover: this.serverPopover,
        errors: {
          required: "The Server URL field is required."
        }
      },
      username: {
        label: "Username",
        popover: "The login of the user.",
        errors: {
          required: "The Username field is required."
        }
      },
      password: {
        label: "Password",
        popover: "The user's password.",
        errors: {
          required: "The Password field is required."
        }
      },
      organizationalUnitName: { label: "Organizational Unit", popover: this.organizationalUnitPopover }
    };
  }
}
